import Vue from "vue";
import { httpsCallable } from "firebase/functions";
import { getOhmyartApp } from "@/ohmyartApp";
import { EventBus, events } from "../event-bus";
import { subscribeUpscaleJob } from "@/upscaleJobs";

/**
 * Laufende Generierungen (generateArtworkV9) und Ergebnisse — außerhalb der Komponente, damit der
 * Generieren-Dialog im Header geschlossen werden kann, während Läufe weiterlaufen, und damit der
 * Header die Anzahl laufender Generierungen anzeigen kann. Mehrere Läufe parallel sind erlaubt.
 */
const STORAGE_KEY = "taskdash_generate_results_v1";
export const MAX_PARALLEL_RUNS = 4;

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const loadResults = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(saved) ? saved.map((r) => ({ ...r, up: { ...(r.up || {}), starting: false } })) : [];
  } catch (e) {
    return [];
  }
};

export const generateRuns = Vue.observable({
  /** laufende Generierungen: { id, startedAt, styleId, version, orientation, inputs, combined } */
  runs: [],
  /** fertige Ergebnisse, neueste zuerst (wie bisher in localStorage) */
  results: loadResults(),
  /** letzter Fehler (wird im Dialog angezeigt, bis der nächste Lauf startet) */
  lastError: "",
});

/**
 * Header-Anzeige (Malte 19.10.): alles, was im Generieren-Dialog gestartet wurde — Generierungen und Upscale-Jobs
 * (auch aus Kunden-Artworks). Läuft etwas: Ladekreis + Anzahl; alles fertig: „fertig/gestartet", bis der Dialog
 * geöffnet wird (ackAt). Upscales überleben ein Neuladen (Job wird neu abonniert), laufende Generierungen nicht.
 */
const ACTIVITY_KEY = "taskdash_generate_activity_v1";
const ACTIVITY_KEEP_MS = 24 * 3600 * 1000;
const loadActivity = () => {
  try {
    const s = JSON.parse(localStorage.getItem(ACTIVITY_KEY) || "{}");
    const items = (Array.isArray(s.items) ? s.items : []).filter((i) => Date.now() - (i.startedAt || 0) < ACTIVITY_KEEP_MS && !(i.type === "gen" && i.status === "running"));
    return { items, ackAt: Number(s.ackAt) || 0 };
  } catch (e) {
    return { items: [], ackAt: 0 };
  }
};
export const generateActivity = Vue.observable(loadActivity());
const persistActivity = () => {
  try {
    localStorage.setItem(ACTIVITY_KEY, JSON.stringify({ items: generateActivity.items.slice(0, 60), ackAt: generateActivity.ackAt }));
  } catch (e) {
    /* voll */
  }
};
const addActivity = (item) => {
  generateActivity.items = [{ status: "running", startedAt: Date.now(), finishedAt: null, ...item }, ...generateActivity.items.filter((i) => i.id !== item.id)];
  persistActivity();
};
const finishActivity = (id, status) => {
  generateActivity.items = generateActivity.items.map((i) => (i.id === id ? { ...i, status, finishedAt: Date.now() } : i));
  persistActivity();
};
/** Dialog geöffnet: fertige Läufe gelten als gesehen */
export const ackActivity = () => {
  generateActivity.ackAt = Date.now();
  persistActivity();
};

const upscaleSubs = {};
/** Upscale-Job für die Header-Anzeige beobachten (idempotent) */
export function trackUpscaleJob(jobId, label) {
  if (!jobId || upscaleSubs[jobId]) return;
  if (!generateActivity.items.some((i) => i.id === jobId)) addActivity({ id: jobId, type: "up", label: label || "Upscale" });
  upscaleSubs[jobId] = subscribeUpscaleJob(
    jobId,
    (job) => {
      if (!job || job.status === "done" || job.status === "failed") {
        finishActivity(jobId, job && job.status === "done" ? "done" : "failed");
        if (upscaleSubs[jobId]) upscaleSubs[jobId]();
        upscaleSubs[jobId] = () => {};
      }
    },
    () => finishActivity(jobId, "failed")
  );
}
// Nach dem Neuladen: noch laufende Upscales wieder abonnieren
generateActivity.items.filter((i) => i.type === "up" && i.status === "running").forEach((i) => trackUpscaleJob(i.id, i.label));

export const persistResults = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(generateRuns.results.slice(0, 40)));
  } catch (e) {
    /* voll */
  }
};

export const removeResult = (id) => {
  generateRuns.results = generateRuns.results.filter((r) => r.id !== id);
  persistResults();
};

/** Generierung starten; läuft unabhängig von der Komponente weiter. Gibt den Lauf zurück (oder null, wenn voll). */
export function startGeneration({ styleId, version, orientation, paths, inputs, meta, overrides }) {
  // Admin-Override (nur wirksam für ADMIN_UIDS in generateArtworkV9): leere Werte = Shop-Standard
  const ov = {};
  if (overrides && ["2K", "4K"].includes(overrides.resolution)) ov.resolution = overrides.resolution;
  if (overrides && ["low", "medium", "high"].includes(overrides.quality)) ov.quality = overrides.quality;
  const hasOverrides = Object.keys(ov).length > 0;
  if (generateRuns.runs.length >= MAX_PARALLEL_RUNS) {
    EventBus.$emit(events.SNACKBAR, { message: `Höchstens ${MAX_PARALLEL_RUNS} Generierungen gleichzeitig`, type: "error" });
    return null;
  }
  const run = { id: uuid(), startedAt: Date.now(), styleId, version, orientation, inputs, combined: paths.length >= 2, overrides: hasOverrides ? ov : null };
  generateRuns.runs = [...generateRuns.runs, run];
  generateRuns.lastError = "";
  addActivity({ id: run.id, type: "gen", label: `${styleId} v${version}` });
  let genStatus = "failed";
  (async () => {
    try {
      const { functions } = getOhmyartApp();
      const call = httpsCallable(functions, "generateArtworkV9", { timeout: 540000 });
      const payload = {
        styleId,
        version,
        aspectRatio: orientation === "landscape" ? "3:2" : "2:3",
        requestId: uuid().replace(/-/g, ""),
        ...(paths.length >= 2 ? { storagePaths: paths, storagePath: paths[0] } : { storagePath: paths[0] }),
        ...(hasOverrides ? { overrides: ov } : {}),
      };
      const { data } = await call(payload);
      if (!data || !data.url) throw new Error("Keine Bild-URL in der Antwort");
      generateRuns.results = [
        {
          id: uuid(),
          at: Date.now(),
          ms: Date.now() - run.startedAt,
          styleId,
          version,
          orientation,
          inputs,
          combined: run.combined,
          url: data.url,
          opaqueUrl: data.opaqueUrl || null,
          docId: data.docId || null,
          requestId: data.requestId || payload.requestId,
          cutout: data.cutout || "none",
          overrides: hasOverrides ? ov : null,
          meta,
          view: "final",
          bg: data.cutout === "done" ? "dark" : "light",
          up: {
            engine: "default",
            resolution: "4K",
            detailPass: !!(meta && meta.print && meta.print.detailPass && meta.print.detailPass.enabled),
            cutout: !!(meta && meta.transparent),
            jobId: null,
            starting: false,
            error: "",
          },
        },
        ...generateRuns.results,
      ];
      persistResults();
      genStatus = "done";
      EventBus.$emit(events.SNACKBAR, { message: `Generierung fertig: ${styleId} v${version}`, type: "success" });
    } catch (e) {
      const message = e.message || String(e);
      generateRuns.lastError = `${styleId} v${version}: ${message}`;
      EventBus.$emit(events.SNACKBAR, { message: `Generierung fehlgeschlagen: ${message}`, type: "error" });
    } finally {
      finishActivity(run.id, genStatus);
      generateRuns.runs = generateRuns.runs.filter((r) => r.id !== run.id);
    }
  })();
  return run;
}
